import type {
  ApiEnvelope,
  BlogPost,
  CMSContent,
  ContactPayload,
  Conversation,
  DomainRecord,
  FileRecord,
  Invoice,
  Message,
  NotificationItem,
  Paginated,
  PortfolioItem,
  Project
} from "../app-types/index";
import { cmsContent, fallbackPortfolioItems } from "./content";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api";

export class ApiError extends Error {
  status: number;
  details?: unknown;

  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.details = details;
  }
}

export interface RequestOptions extends RequestInit {
  token?: string | null;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers = new Headers(options.headers);

  if (!(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  if (options.token) {
    headers.set("Authorization", `Bearer ${options.token}`);
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers
  });

  if (!response.ok) {
    let payload: unknown;
    try {
      payload = await response.json();
    } catch {
      payload = await response.text();
    }
    throw new ApiError(`Request failed with ${response.status}`, response.status, payload);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const envelope = (await response.json()) as ApiEnvelope<T>;
  return envelope.data;
}

function localPortfolio(params = ""): Paginated<PortfolioItem> {
  const searchParams = new URLSearchParams(params.startsWith("?") ? params.slice(1) : params);
  const page = Math.max(1, Number(searchParams.get("page") || "1"));
  const pageSize = Math.min(24, Math.max(1, Number(searchParams.get("pageSize") || "6")));
  const search = (searchParams.get("search") || "").trim().toLowerCase();
  const category = (searchParams.get("category") || "all").trim().toLowerCase();

  let items = fallbackPortfolioItems;

  if (category && category !== "all") {
    items = items.filter((item) => item.category.toLowerCase() === category);
  }

  if (search) {
    items = items.filter((item) => {
      const haystack = [item.title, item.summary, item.category, item.demoUrl || "", ...item.services].join(" ").toLowerCase();
      return haystack.includes(search);
    });
  }

  const total = items.length;
  const start = (page - 1) * pageSize;

  return {
    items: items.slice(start, start + pageSize),
    page,
    pageSize,
    total
  };
}

export const api = {
  async getCMS() {
    try {
      const remote = await request<Partial<CMSContent>>("/admin/cms/public");
      return {
        ...cmsContent,
        ...remote,
        homepage: { ...cmsContent.homepage, ...remote.homepage },
        footer: { ...cmsContent.footer, ...remote.footer },
        services: remote.services || cmsContent.services,
        pricing: remote.pricing || cmsContent.pricing,
        faq: remote.faq || cmsContent.faq
      };
    } catch {
      return cmsContent;
    }
  },
  async getPortfolio(params = "") {
    try {
      const remote = await request<Paginated<PortfolioItem>>(`/portfolio${params}`);
      return remote.items.length > 0 ? remote : localPortfolio(params);
    } catch {
      return localPortfolio(params);
    }
  },
  getBlog(params = "") {
    return request<Paginated<BlogPost>>(`/blog${params}`);
  },
  getProjects(token: string) {
    return request<Project[]>("/projects", { token });
  },
  getInvoices(token: string) {
    return request<Invoice[]>("/invoices", { token });
  },
  getNotifications(token: string) {
    return request<NotificationItem[]>("/notifications", { token });
  },
  getDomains(token: string) {
    return request<DomainRecord[]>("/domains", { token });
  },
  getFiles(token: string) {
    return request<FileRecord[]>("/files", { token });
  },
  getConversations(token: string) {
    return request<Conversation[]>("/messages/conversations", { token });
  },
  getMessages(token: string, conversationId: string) {
    return request<Message[]>(`/messages?conversationId=${encodeURIComponent(conversationId)}`, { token });
  },
  sendMessage(token: string, payload: { conversationId: string; body: string; fileId?: string }) {
    return request<Message>("/messages", {
      method: "POST",
      token,
      body: JSON.stringify(payload)
    });
  },
  markRead(token: string, messageId: string) {
    return request<Message>(`/messages/${messageId}/read`, {
      method: "PATCH",
      token
    });
  },
  sendTyping(token: string, conversationId: string) {
    return request<{ ok: true }>("/messages/typing", {
      method: "POST",
      token,
      body: JSON.stringify({ conversationId })
    });
  },
  uploadFile(token: string, file: File, projectId?: string) {
    const form = new FormData();
    form.append("file", file);
    if (projectId) {
      form.append("projectId", projectId);
    }

    return request<FileRecord>("/files", {
      method: "POST",
      token,
      body: form
    });
  },
  submitContact(payload: ContactPayload) {
    return request<{ ok: true; conversationId: string }>("/contact", {
      method: "POST",
      body: JSON.stringify(payload)
    });
  },
  reportIssue(token: string, payload: { message: string; page: string }) {
    return request<{ ok: true }>("/issues/report", {
      method: "POST",
      token,
      body: JSON.stringify(payload)
    });
  },
  adminAnalytics(token: string) {
    return request<{
      users: number;
      projects: number;
      conversations: number;
      invoicesOutstandingCents: number;
      portfolio: number;
      posts: number;
    }>("/admin/analytics", { token });
  },
  adminList<T>(token: string, resource: "users" | "projects" | "portfolio" | "blog" | "invoices" | "domains") {
    return request<T[]>(`/admin/${resource}`, { token });
  },
  upsertCMS(token: string, section: string, value: unknown) {
    return request<CMSContent>("/admin/cms", {
      method: "PUT",
      token,
      body: JSON.stringify({ section, value })
    });
  }
};
